import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Code,
  School,
  CheckCircle,
  ArrowForward,
  ArrowBack,
  Email,
} from '@mui/icons-material';
import courseCodeService from '../services/courseCodeService';
import authService from '../services/authService';
import type { CourseCode, CourseCodeRegistrationRequest } from '../types';

interface CourseCodeRegistrationProps {
  userType: 'student' | 'parent';
  userId: string;
  childId?: string; // For parent users
}

interface CourseDetails {
  id: string;
  name: string;
  description: string;
  type: string;
  instructor: string;
  duration: string;
  subjects: string[];
}

const CourseCodeRegistration: React.FC<CourseCodeRegistrationProps> = ({
  userType,
  userId,
  childId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [courseCode, setCourseCode] = useState('');
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [validatedCode, setValidatedCode] = useState<CourseCode | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const steps = [
    'Enter Course Code',
    'Verify Course Details',
    'Confirm Registration',
  ];

  const handleNext = () => {
    if (activeStep === 0 && !courseCode.trim()) {
      setError('Please enter a course code');
      return;
    }
    
    if (activeStep === 0) {
      validateCourseCode();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateCourseCode = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const codeData = await courseCodeService.validateCode(courseCode.trim());
      setValidatedCode(codeData);
      
      // Map CourseCode to CourseDetails format
      const course = codeData.course;
      const courseDetailsData: CourseDetails = {
        id: course.id.toString(),
        name: course.name,
        description: course.description || 'No description available',
        type: course.courseType?.replace('_', ' ') || 'Course',
        instructor: course.instructor || 'TBD',
        duration: course.durationWeeks ? `${course.durationWeeks} weeks` : 'Ongoing',
        subjects: [course.courseType?.replace('_', ' ') || 'General'],
      };
      
      setCourseDetails(courseDetailsData);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.message || 'Invalid course code. Please check and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRegistration = async () => {
    if (!validatedCode) {
      setError('Please validate a course code first');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const targetUserId = childId ? parseInt(childId, 10) : parseInt(userId, 10);
      const currentUser = authService.getCurrentUser();
      
      const registrationRequest: CourseCodeRegistrationRequest = {
        courseCode: validatedCode.code,
        userId: targetUserId,
        enrolledBy: currentUser?.email || 'self',
      };
      
      await courseCodeService.registerWithCode(registrationRequest);
      
      setSuccess('Successfully registered for the course!');
      setActiveStep(2);
      
      // Navigate to dashboard after 2 seconds
      setTimeout(() => {
        if (childId) {
          navigate('/parent-dashboard');
        } else {
          navigate('/student-dashboard');
        }
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Code sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2 }}>
                Register with Course Code
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {userType === 'parent' 
                  ? 'Enter the course code to register your child for a course'
                  : 'Enter the course code provided by your teacher or tutor'
                }
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              label="Course Code"
              value={courseCode}
              onChange={(e) => setCourseCode(e.target.value)}
              placeholder="e.g., MATH2024-S3"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <Code sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Where to find your course code:</strong>
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  From your teacher or tutor
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  In course invitation emails
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  On course materials or handouts
                </Typography>
              </Box>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <School sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2 }}>
                Course Details
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please review the course details before confirming registration
              </Typography>
            </Box>
            
            {courseDetails && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Course Name</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {courseDetails.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Course Type</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {courseDetails.type}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Description</Typography>
                      <Typography variant="body1">
                        {courseDetails.description}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Instructor</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {courseDetails.instructor}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {courseDetails.duration}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Subjects Covered</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {courseDetails.subjects.map((subject, index) => (
                          <Box
                            key={index}
                            sx={{
                              px: 2,
                              py: 1,
                              bgcolor: 'primary.main',
                              color: 'white',
                              borderRadius: 2,
                              fontSize: '0.875rem',
                            }}
                          >
                            {subject}
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
            
            <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant="body2" color="info.main">
                <strong>Note:</strong> By registering for this course, you agree to participate in all 
                required activities and assessments. Course materials will be available after successful registration.
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" sx={{ mb: 2, color: 'success.main' }}>
              Registration Successful!
            </Typography>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {userType === 'parent' 
                ? 'Your child has been registered for the course'
                : 'You have been registered for the course'
              }
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              You will receive an email confirmation shortly. Course materials and access will be available 
              within 24 hours.
            </Typography>
            
            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, maxWidth: 400, mx: 'auto' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Next Steps:</strong>
              </Typography>
              <Box component="ul" sx={{ pl: 2, textAlign: 'left' }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  Check your email for course details
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Access course materials from your dashboard
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Complete any required assessments
                </Typography>
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
        <Typography variant="h4" sx={{ mb: 2, color: 'success.main' }}>
          Registration Complete!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {success}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.href = '/dashboard'}
          size="large"
        >
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 4 }}>
      <Card>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4, display: { xs: 'none', md: 'flex' } }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Mobile Step Indicator */}
          {isMobile && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Step {activeStep + 1} of {steps.length}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {steps[activeStep]}
              </Typography>
            </Box>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          {renderStepContent()}

          {/* Navigation Buttons */}
          {activeStep < 2 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ display: activeStep === 0 ? 'none' : 'flex' }}
              >
                Back
              </Button>
              <Box sx={{ flex: '1' }} />
              <Button
                variant="contained"
                onClick={activeStep === 1 ? handleConfirmRegistration : handleNext}
                endIcon={activeStep === 1 ? <CheckCircle /> : <ArrowForward />}
                size="large"
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : activeStep === 1 ? 'Confirm Registration' : 'Next'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CourseCodeRegistration;
